import { useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  Captions,
  LogOut,
  Mic,
  MicOff,
  PhoneOff,
  Sparkles,
  UserX,
  Users,
  Video,
  VideoOff,
} from 'lucide-react';

import { RecordingControl } from './RecordingControl';

export type SidePanelId = 'transcript' | 'assistant' | 'participants';

interface CallControlBarProps {
  mode: 'online' | 'helpline';
  showRecordingControl: boolean;
  showTranscriptToggle: boolean;
  showAssistantToggle: boolean;
  showParticipantsToggle: boolean;
  isPaused: boolean;
  isRecordingPending: boolean;
  openPanel: SidePanelId | null;
  participantCount: number;
  onLeave: () => void;
  onLeaveAndComplete?: () => void;
  /** Practitioner-only. Ends the call and records the session as a no-show.
   *  Kept visually distinct from Leave & complete so the two outcomes can't
   *  be confused at a glance. */
  onMarkNoShow?: () => void;
  onStartRecording?: () => void;
  onStopRecording?: () => void;
  /** Toggle a side rail. Pass the same id to close it (caller decides). */
  onTogglePanel: (id: SidePanelId) => void;
}

export function CallControlBar({
  mode,
  showRecordingControl,
  showTranscriptToggle,
  showAssistantToggle,
  showParticipantsToggle,
  isPaused,
  isRecordingPending,
  openPanel,
  participantCount,
  onLeave,
  onLeaveAndComplete,
  onMarkNoShow,
  onStartRecording,
  onStopRecording,
  onTogglePanel,
}: CallControlBarProps) {
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 flex justify-center">
      <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/8 bg-[#0F1F1C]/80 p-1.5 shadow-[0_12px_32px_rgba(0,0,0,0.4)] backdrop-blur-xl">
        <MicToggle />
        {mode === 'online' ? <CameraToggle /> : null}

        {showRecordingControl && onStartRecording != null && onStopRecording != null ? (
          <>
            <Divider />
            <RecordingControl
              isPaused={isPaused}
              isPending={isRecordingPending}
              onPause={onStopRecording}
              onResume={onStartRecording}
            />
          </>
        ) : null}

        {showTranscriptToggle ? (
          <>
            <Divider />
            <ControlPill
              active={openPanel === 'transcript'}
              onClick={() => {
                onTogglePanel('transcript');
              }}
              aria-pressed={openPanel === 'transcript'}
              aria-label="Toggle transcript panel"
            >
              <Captions className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
              <span className="hidden text-[13px] @xl:inline">Transcript</span>
            </ControlPill>
          </>
        ) : null}

        {showAssistantToggle ? (
          <ControlPill
            active={openPanel === 'assistant'}
            onClick={() => {
              onTogglePanel('assistant');
            }}
            aria-pressed={openPanel === 'assistant'}
            aria-label="Toggle Ask Elixaa panel"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
            <span className="hidden text-[13px] @xl:inline">Ask Elixaa</span>
          </ControlPill>
        ) : null}

        {showParticipantsToggle ? (
          <ControlPill
            active={openPanel === 'participants'}
            onClick={() => {
              onTogglePanel('participants');
            }}
            aria-pressed={openPanel === 'participants'}
            aria-label="Toggle participants panel"
          >
            <Users className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
            <span className="hidden text-[13px] @xl:inline">Participants</span>
            {participantCount > 0 ? (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/10 px-1.5 text-[11px] font-medium text-white/85">
                {participantCount}
              </span>
            ) : null}
          </ControlPill>
        ) : null}

        <Divider />
        <button
          type="button"
          onClick={onLeave}
          aria-label="Leave call"
          className="inline-flex h-10 items-center gap-2 rounded-full bg-white/8 px-3 text-[13px] font-medium text-white/85 transition-colors hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F1F1C] focus-visible:outline-none @xl:px-4"
        >
          <LogOut className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
          <span className="hidden @xl:inline">Leave</span>
        </button>
        {onMarkNoShow ? (
          <button
            type="button"
            onClick={onMarkNoShow}
            aria-label="Mark session as no-show and leave"
            className="inline-flex h-10 items-center gap-2 rounded-full border border-white/20 px-3 text-[13px] font-medium text-white/85 transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F1F1C] focus-visible:outline-none @xl:px-4"
          >
            <UserX className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
            <span className="hidden @xl:inline">No-show</span>
          </button>
        ) : null}
        {onLeaveAndComplete ? (
          <button
            type="button"
            onClick={onLeaveAndComplete}
            aria-label="Leave and complete session"
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[#9C3B2E] px-3 text-[13px] font-medium text-white transition-colors hover:bg-[#B04638] focus-visible:ring-2 focus-visible:ring-[#B04638] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F1F1C] focus-visible:outline-none @xl:px-4"
          >
            <PhoneOff className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
            <span className="hidden @xl:inline">Leave &amp; complete</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MicToggle() {
  const { enabled, buttonProps } = useTrackToggle({ source: Track.Source.Microphone });
  const { className: _lkClass, ...cleanButtonProps } = buttonProps;
  return (
    <ControlPill
      {...cleanButtonProps}
      active={!enabled}
      aria-label={enabled ? 'Mute microphone' : 'Unmute microphone'}
    >
      {enabled ? (
        <Mic className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
      ) : (
        <MicOff className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
      )}
      <span className="hidden text-[13px] @xl:inline">{enabled ? 'Mic on' : 'Muted'}</span>
    </ControlPill>
  );
}

function CameraToggle() {
  const { enabled, buttonProps } = useTrackToggle({ source: Track.Source.Camera });
  const { className: _lkClass, ...cleanButtonProps } = buttonProps;
  return (
    <ControlPill
      {...cleanButtonProps}
      active={!enabled}
      aria-label={enabled ? 'Turn camera off' : 'Turn camera on'}
    >
      {enabled ? (
        <Video className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
      ) : (
        <VideoOff className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
      )}
      <span className="hidden text-[13px] @xl:inline">{enabled ? 'Camera' : 'Camera off'}</span>
    </ControlPill>
  );
}

interface ControlPillProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  children: React.ReactNode;
}

function ControlPill({ active = false, className, children, ...rest }: ControlPillProps) {
  return (
    <button
      type="button"
      {...rest}
      className={`inline-flex h-10 items-center gap-2 rounded-full px-3.5 text-white/85 transition-colors focus-visible:ring-2 focus-visible:ring-[#35816E] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F1F1C] focus-visible:outline-none ${
        active ? 'bg-white/15 text-white' : 'hover:bg-white/8'
      } ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden="true" className="mx-0.5 h-5 w-px bg-white/10" />;
}
